import { Component, QueryList, ViewChildren } from '@angular/core';
import { OnInit, OnDestroy, ViewChild } from '@angular/core';
import { Subscription } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';

import { AgGridAngular } from 'ag-grid-angular';
import { GridOptions, SortChangedEvent, PaginationChangedEvent, ColumnResizedEvent, ColumnMovedEvent, ColumnVisibleEvent, Column } from 'ag-grid-community';
import { GridCellRendererComponent } from '../grid-cell-renderer/grid-cell-renderer.component';
import { GridTitleCellRendererComponent } from '../grid-title-cell-renderer/grid-title-cell-renderer.component';
import { GridCheckboxCellRendererComponent } from '../grid-checkbox-cell-renderer/grid-checkbox-cell-renderer.component';
import { GridLinkCellRendererComponent } from '../grid-link-cell-renderer/grid-link-cell-renderer.component';
import { GridSearchableCellRendererComponent } from '../grid-searchable-cell-renderer/grid-searchable-cell-renderer.component';
import { GridMenuCellRendererComponent } from '../grid-menu-cell-renderer/grid-menu-cell-renderer.component';
import { GridIconsCellRendererComponent } from '../grid-icons-cell-renderer/grid-icons-cell-renderer.component';
import { GridToolsHeaderRendererComponent } from '../grid-tools-header-renderer/grid-tools-header-renderer.component';

import { ConfigService } from '../../services/config.service';
import { UserService } from '../../services/user.service';
import { RoutingService } from '../../services/routing.service';
import { CartService } from '../../cart/services/cart.service';
import { SearchService } from '../../search/services/search.service';
import { MapService } from '../../map/services/map.service';
import { DocViewerFactoryService } from '../../services/factory/doc-viewer-factory.service';
import { MetadataQuickViewComponent } from '../../views/metadata-quick-view/metadata-quick-view.component';
import { RecordQuickViewComponent } from '../../records-management/components/record-quick-view/record-quick-view.component';
import { TagsComponent } from '../../components/tags/tags.component';
import { CopyLinkComponent } from '../../components/copy-link/copy-link.component';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';

/**
 * Grid search results view.
 * Grid is implemented using AG Grid
 * API ref: https://www.ag-grid.com/angular-data-grid/grid-api
 * Event API ref: https://www.ag-grid.com/angular-data-grid/grid-events
 * Column properties ref: https://www.ag-grid.com/angular-data-grid/column-properties 
 */
@Component({
	selector: 'grid-view',
	templateUrl: './grid-view.component.html',
	styleUrl: './grid-view.component.css'
})
export class GridViewComponent implements OnInit, OnDestroy {
	@ViewChild('kvGrid') grid!: AgGridAngular;
	@ViewChildren(NgbDropdown) dropdowns!: QueryList<NgbDropdown>;

	private userEventSubscription!: Subscription;
	private searchEventSubscription!: Subscription;
	private routingEventSubscription!: Subscription;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	viewType?: string; // Corresponds to $stateParams.v

	records: any[] = [];

	validFields: any[] = [];
	validResultFields: any[] = [];
	searchableResultFields: any[] = [];

	visited: { [key: string]: any } = {};
	tagsEnabled: boolean = false;
	cartOn: boolean = false;
	mapViewEnabled: boolean = false;
	additionalIcons: boolean = false;
	isMoreLikeThisEnabled: boolean = false;
	streamingQueries: { [key: string]: any } = {};
	isUpdateGridSortEvent: boolean = false;

	// -- Grid options --
	// Properties applied to all columns
	defaultColDef = {
		flex: 1,
		filter: false
	};

	columns: any[] = [];

	gridOptions: GridOptions = {
		context: {
			openRecord: this.openRecord.bind(this),
			displayLinkMsg: this.displayLinkMsg.bind(this),
			openInMap: this.openInMap.bind(this),
			viewMetadata: this.viewMetadata.bind(this),
			searchSimilarDocs: this.searchSimilarDocs.bind(this),
			addToCart: this.addToCart.bind(this),
			viewTags: this.viewTags.bind(this),
			viewRMQuickInfo: this.viewRMQuickInfo.bind(this),
			searchByStreamingQuery: this.searchByStreamingQuery.bind(this),
			visited: this.visited,
			mapViewEnabled: this.mapViewEnabled,
			isMoreLikeThisEnabled: this.isMoreLikeThisEnabled,
			cartOn: this.cartOn,
			tagsEnabled: this.tagsEnabled,
			streamingQueries: this.streamingQueries
		}
	};

	constructor(
		private ngbModal: NgbModal,
		private userService: UserService,
		private configService: ConfigService,
		private cartService: CartService,
		private routingService: RoutingService,
		private searchService: SearchService,
		private mapService: MapService,
		private docViewerFactory: DocViewerFactoryService
	) {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		this.buildGridColumns();
	}

	ngOnInit(): void {
		this.userEventSubscription = this.userService.broadcastEvent.subscribe((event: string) => {
			if (event === 'loggedin') {
				this.processSearchResults();
				this.checkCartButtons();
			}
			else if (event === 'localizationLoaded') {
				this.LOCALIZATION = this.userService.LOCALIZATION;
			}
		});

		this.searchEventSubscription = this.searchService.broadcastEvent.subscribe(event => {
			if (event.type === 'searchEnded') {
				const receivedTime = Date.now();
				this.processSearchResults();
			}
			else if (event.type === 'clearSearch') {
				this.records = [];
			}
			else if (event.type === 'updateGridSort' && this.grid) {
				let column = this.columns.find((col: any) => { return col.field === event.data.field.name; });
				if(column){
					 this.isUpdateGridSortEvent = true;
					 this.grid.api!.applyColumnState({
					  state: [
					    { colId: event.data.field.name, sort: event.data.direction.toLowerCase() } // 'asc' or 'desc'
					  ],
					  defaultState: { sort: null }, // clears sort from all others
					  applyOrder: true
					});
				}
			}
		});

		// Listen for route changes, specifically related to the view type
		this.routingEventSubscription = this.routingService.routingEvent.subscribe(event => {
			if (event.eventType === 'navigationEnded') {
				this.processSearchResults();
			}
		});

		this.searchableResultFields = this.configService.getConfig().search.searchableResultFields;
		this.mapViewEnabled = this.configService.getConfig().esri.enabled;
		this.tagsEnabled = this.configService.getConfig()?.menu?.tags;
		this.isMoreLikeThisEnabled = this.configService.getConfig().search.isMoreLikeThis;
		this.streamingQueries = this.configService.getConfig().search.streamingQueries;

		this.visited = this.userService.getLocalStorageData().visited;
		let property = this.userService.user.properties.awpropertyObjectList.find((x: any) => { return x.name === "DISPLAY_ADDITIONAL_ICONS"; });
		this.additionalIcons = property?.value === "true" ? true : false;

		this.checkCartButtons();

		this.gridOptions.context.visited = this.visited;
		this.gridOptions.context.mapViewEnabled = this.mapViewEnabled;
		this.gridOptions.context.isMoreLikeThisEnabled = this.isMoreLikeThisEnabled;
		this.gridOptions.context.cartOn = this.cartOn;
		this.gridOptions.context.tagsEnabled = this.tagsEnabled;
		this.gridOptions.context.streamingQueries = this.streamingQueries;
		this.processSearchResults();
	}

	ngOnDestroy() {
		if (this.userEventSubscription) {
			this.userEventSubscription.unsubscribe();
		}

		if (this.routingEventSubscription) {
			this.routingEventSubscription.unsubscribe();
		}

		if (this.searchEventSubscription) {
			this.searchEventSubscription.unsubscribe();
		}
	}

	private checkCartButtons(): void {
		if (this.userPermissions['ROLE_PRODUCT_DOWNLOAD_RESTRICTED'] && !this.userPermissions["ROLE_PRODUCT_DOWNLOAD"]) {
			this.cartOn = true;
		} else {
			this.cartOn = false;
		}
	}

	private processSearchResults(): void {
		this.viewType = this.routingService.getQueryParam('v');
		if (!this.viewType || this.viewType !== 'G') {
			return;
		}

		console.log('Processing search results!');

		let results = this.searchService.results;

		if (!results || results.error) {
			this.records = [];
			return;
		}

		if (this.validFields.length == 0) {
			if (!this.userService.user.properties) return;
			this.getValidFields();
		}

		this.refreshGridColumns();
		this.records = this.searchService.processSearchResults(results, this.validFields);
	}

	private getValidFields(): void {
		this.validFields = this.userService.user.properties.awfieldObjectList.filter((x: any) => {
			return x.isAvailable && (x.isSearch || x.isResult);
		});

		this.validResultFields = this.validFields.filter(function(x) {
			return x.isResult;
		});

		this.userService.getUserExtendedPrefs().then(
			(userPrefsObj: any) => {
				// Reorder the fields based on these settings
				let prefs: any = {};
				try {
					const rawPrefs = userPrefsObj.prefs || '{}';
					prefs = JSON.parse(rawPrefs);
				} catch (e) {
					console.error('Invalid JSON in userPrefsObj.prefs:', e);
					prefs = {}; // fallback to empty object
				}
				let resultorder = prefs.resultFieldOrder || [];
				this.validResultFields.sort((a: any, b: any) => {
					const indexA = resultorder.indexOf(a.name);
					const indexB = resultorder.indexOf(b.name);

					if (a.field == 'aw_rank' || b.field == 'aw_rank' ||
						a.name == '_index' || b.name == '_index' ||
						a.name == 'title' || b.name == 'title') {
						return 0;
					}
					else if (indexA === -1 && indexB === -1) {
						return 0;
					} else if (indexA === -1) {
						return 1;
					} else if (indexB === -1) {
						return -1;
					} else {
						return indexA - indexB;
					}
				});
			}
		);
	}

	isValidFieldCollectionValue(field: string, value: string): boolean {
		return this.searchService.isValidCollectionValue(field, value);
	}

	/**
	 * Displays the product in the document viewer.
	 */
	openRecord(id: number): void {
		const viewerComponent = this.docViewerFactory.getViewerComponent();
		const modalRef = this.ngbModal.open(viewerComponent, {
			windowClass: "viewer-modal-window"
		});

		modalRef.componentInstance.productId = id;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);

		let userData = this.userService.getLocalStorageData();
		this.visited = userData.visited;
	}

	/**
	 * Displays the full metadata for the product.
	 */
	viewMetadata(prodid: number): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(MetadataQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the record management metadata for the product.
	 */
	viewRMQuickInfo(prodid: number): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(RecordQuickViewComponent);
		modalRef.componentInstance.productId = prodid;

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the tag manager for the product.
	 */
	viewTags(record: any): void {
		this.closeAllDropdowns();
		const modalRef = this.ngbModal.open(TagsComponent);
		modalRef.componentInstance.product = {
			id: record.id,
			title: record.title
		};

		modalRef.result.then(
			(payload) => {
				console.log('Modal dismissed');
			},
			(reason) => {
				console.log('Modal dismissed');
			}
		);
	}

	/**
	 * Displays the product in the map view.
	 */
	openInMap(record: any): void {
		this.mapService.openInMap(record, false);
	}

	displayLinkMsg(link: string): void {
		if (link.startsWith("file://")) {
			const modalRef = this.ngbModal.open(CopyLinkComponent);
			modalRef.componentInstance.link = link;

			modalRef.result.then(
				(payload) => {
					console.log('Modal dismissed');
				},
				(reason) => {
					console.log('Modal dismissed');
				}
			);
		}
	}

	/**
	 * Executes a search for documents similar to this one.
	 */
	searchSimilarDocs(product: any): void {
		this.closeAllDropdowns();
		this.searchService.similaritySearch(product.id, product.title);
	}

	searchByStreamingQuery(product: any, streamingQueryType: string, displayName: string): void {
		this.closeAllDropdowns();
		this.searchService.sreamingQuerySearch(product.id, product.title, streamingQueryType, displayName);
	}

	addToCart(record: any): void {
		this.closeAllDropdowns();
		this.cartService.addToCart(record.id, record.title);
	}


	/**
	 * 
	 * Grid-related functionality.
	 * 
	 */
	onGridReady(event: any) {
		this.grid.api.addEventListener('sortChanged', this.onSortChanged.bind(this));
		this.grid.api.addEventListener('columnResized', this.onColumnResized.bind(this));
		this.grid.api.addEventListener('columnMoved', this.onColumnMoved.bind(this));
		this.grid.api.addEventListener('modelUpdated', this.resizeGridColumns.bind(this));
		this.grid.api.addEventListener('columnVisible', this.onColumnVisible.bind(this));
		this.grid.api!.applyColumnState({
		  defaultState: { sort: null }
		});
		// Initial resize
		this.resizeGridColumns();
	}

	private onSortChanged(event: SortChangedEvent): void {
		if(!this.isUpdateGridSortEvent){
		let sortedColumn: Column | undefined = event?.columns?.[event.columns.length - 1];
		let field: string = '';
		let direction: string | null = null;

		if (sortedColumn && sortedColumn.getSort()) {
			console.log('Sorting changed:', `${sortedColumn.getColId()},${sortedColumn.getSort()}`);

			direction = sortedColumn.getSort()?.toUpperCase() || null;
			field = sortedColumn.getColId() == "aw_rank" ? "AW5_SORT_BY_FINE_RANK" : sortedColumn.getColId();
			if (field === this.searchService.sortField && direction === this.searchService.sortDirection) {
				direction = direction == "ASC" ? "DESC" : "ASC";
			}
		} else {
			console.log('Sorting changed: Blank - Defaulting to user prefs sort');
			// Revert to default user prefs sort
			let defaultSort =
				this.userService.user.properties?.awsortObjectList?.length > 0 ?
					this.userService.user.properties.awsortObjectList[0] :
					{ fieldName: "AW5_SORT_BY_FINE_RANK", isAscending: false };

			field = defaultSort.fieldName;
			direction = defaultSort.isAscending ? 'ASC' : 'DESC';
		}

		if (this.configService.getConfig().search.multiFacetSearch) {
			this.searchService.broadcastEvent.emit({ type: 'facetPreserved', data: {} });
		}

		this.searchService.update_pagingSorting({
			pageSize: this.searchService.pageSize,
			pageIndex: 1,
			sortDirection: direction,
			sortField: field
		});
		} else {
			this.isUpdateGridSortEvent = false;
		}
	}

	private onColumnMoved(event: ColumnMovedEvent) {
		if (event.finished && event.source === 'uiColumnMoved') {
			const columnState = this.grid.api.getColumnState();

			let userData = this.userService.getLocalStorageData();
			userData.cols = columnState;
			this.userService.setLocalStorageData(userData);
		}
	}

	private onColumnResized(event: ColumnResizedEvent) {
		if (event.finished && event.source === 'uiColumnResized') {
			const columnState = this.grid.api.getColumnState();

			let userData = this.userService.getLocalStorageData();
			userData.cols = columnState;
			this.userService.setLocalStorageData(userData);
		}
	}

	private resizeGridColumns(): void {
		if (!this.grid.api) return;

		let userData = this.userService.getLocalStorageData();
		let savedColumnState = Array.isArray(userData?.cols) ? userData.cols : [];

		if (savedColumnState.length > 0) {
			const widthOnlyState = savedColumnState.map((col: any) => ({
				colId: col.colId,
				width: col.width
			}));

			// Reapply only the column widths
			const success = this.grid.api.applyColumnState({
				state: widthOnlyState,
				applyOrder: false
			});
			
			// The icons column should always be auto sized
			this.grid.api.autoSizeColumns(['_icons']);

			if (!success) {
				console.warn('Failed to apply saved column state');
				this.autoSizeAllVisibleColumns();
			}
		} else {
			this.autoSizeAllVisibleColumns();
		}
	}

	/**
	 * Auto-sizes all visible columns based on content.
	 */
	private autoSizeAllVisibleColumns(): void {
		const allVisibleColumnIds = this.grid.api.getAllDisplayedColumns().map((col: any) => col.getColId());

		this.grid.api.autoSizeColumns(allVisibleColumnIds);
	}

	/**
	 * Sets equal column width based on grid width and number of visible columns.
	 */
	private setEqualWidthForAllVisibleColumns(): void {
		const visibleCols = this.grid.api.getAllDisplayedColumns();

		// Get grid container width
		const gridWidth = (document.querySelector('#grid1') as HTMLElement)?.offsetWidth || 1000;

		// Calculate equal width
		const equalWidth = Math.floor(gridWidth / visibleCols.length);
		
		// Some column widths should remain fixed
		const fixedWidthCols = ['id', '_icons', '_buttons'];

		// Build column state array
		const equalWidthState = visibleCols.map((col: any) => ({
			colId: col.getColId(),
			width: (fixedWidthCols.includes(col.getColId()) ? col.getActualWidth() : equalWidth)
		}));

		// Apply the equal widths
		this.grid.api.applyColumnState({
			state: equalWidthState,
			applyOrder: false
		});
		
		// The icons column should always be auto sized
		this.grid.api.autoSizeColumns(['_icons']);
	}

	private refreshGridColumns(): void {
		this.reorderGridColumns();
		this.refreshGridOptions();
		this.updateGridColumnVisibility();
	}

	private onColumnVisible(event: ColumnVisibleEvent) {
		const columnState = this.grid.api.getColumnState();

		let userData = this.userService.getLocalStorageData();
		userData.cols = columnState;
		this.userService.setLocalStorageData(userData);
	}

	private updateGridColumnVisibility(): void {
		let userData = this.userService.getLocalStorageData();
		let columnState = Array.isArray(userData?.cols) ? userData.cols : [];

		for (let y = 0; y < this.validFields.length; y++) {
			let field = this.validFields[y];
			if (field.name === 'title') continue;

			let visible = true;
			if (!field.isResult) {
				visible = false;
			}

			let column = this.columns.find((col: any) => { return col.field === field.name; });
			if (column) {
				column.hide = !visible;
			}

			// Override with new visibility, if any
			let currentColumnState = columnState.find((col: any) => { return col.colId === field.name; });
			if (currentColumnState && column) {
				column.hide = currentColumnState.hide;
			}
		}

		if (this.additionalIcons) {
			let column = this.columns.find((col: any) => { return col.colId === '_icons'; });
			if (column) {
				column.hide = false;
			}
		}
	}

	private reorderGridColumns(): void {
		let fieldOrderMap = new Map<string, number>();
		this.validResultFields.forEach((field: any, index: number) => {
			fieldOrderMap.set(field.name, index);
		});

		// Separate columns that should not be reordered from the rest
		const pinnedOrder = ['id', '_icons', 'title'];
		let pinnedColumns = this.columns
			.filter((col: any) => pinnedOrder.includes(col.colId))
			.sort((a: any, b: any) => pinnedOrder.indexOf(a.colId) - pinnedOrder.indexOf(b.colId));

		let sortableColumns = this.columns.filter((col: any) => (!pinnedOrder.includes(col.colId)));

		// Initially sort the remaining columns based on validResultFields order
		sortableColumns.sort((a: any, b: any) => {
			const orderA = fieldOrderMap.has(a.field) ? fieldOrderMap.get(a.field) : Infinity;
			const orderB = fieldOrderMap.has(b.field) ? fieldOrderMap.get(b.field) : Infinity;
			return (orderA as number) - (orderB as number);
		});

		// Sort columns based on saved options, if those exist
		let userData = this.userService.getLocalStorageData();
		let columnState = Array.isArray(userData?.cols) ? userData.cols : [];

		fieldOrderMap = new Map<string, number>();
		columnState.forEach((column: any, index: number) => {
			fieldOrderMap.set(column.colId, index);
		});
		sortableColumns.sort((a: any, b: any) => {
			const orderA = fieldOrderMap.has(a.field) ? fieldOrderMap.get(a.field) : Infinity;
			const orderB = fieldOrderMap.has(b.field) ? fieldOrderMap.get(b.field) : Infinity;
			return (orderA as number) - (orderB as number);
		});

		this.columns = [...pinnedColumns, ...sortableColumns];
	}

	private refreshGridOptions(): void {
		let userData = this.userService.getLocalStorageData();
		this.visited = userData.visited;
		this.gridOptions.context.visited = this.visited;
	}

	private buildGridColumns(): void {
		if (this.validFields.length == 0) {
			if (!this.userService.user.properties) return;
			this.getValidFields();
		}

		this.columns = [];

		// -- Checkbox Column --
		this.columns.push({
			field: 'id',
			colId: 'id',
			headerName: '',
			width: 35,
			suppressHeaderMenuButton: true,
			sortable: true,
			comparator: () => 0,  // disables actual sorting
			resizable: false,
			pinned: 'left',
			lockPosition: true,
			suppressMovable: true,
			suppressSizeToFit: true,
			cellRenderer: GridCheckboxCellRendererComponent
		});

		// -- Additional Icons column --
		this.columns.push({
			colId: '_icons',
			headerName: '',
			cellRenderer: GridIconsCellRendererComponent,
			hide: true,
			width: 60,
			suppressHeaderMenuButton: true,
			sortable: false,
			pinned: 'left',
			lockPosition: true,
			suppressMovable: true
		});

		// -- Title --
		// Title is always displayed in the grid
		let titleField = this.validFields.find((field: any) => field.name === "title");
		this.columns.push({
			field: titleField.name,
			colId: 'title',
			suppressNavigable: true,
			headerName: (titleField.displayName || this.LOCALIZATION.title),
			cellRenderer: GridTitleCellRendererComponent,
			pinned: 'left',
			lockPosition: true,
			suppressMovable: true,
			sortable: true,
			comparator: () => 0  // disables actual sorting
		});


		// -- All other available fields --
		for (let y = 0; y < this.validFields.length; y++) {
			let field = this.validFields[y];
			if (field.name === 'title') continue;

			let visible = true;
			if (!field.isResult) {
				visible = false;
			}

			let def = {
				field: field.name,
				colId: field.name,
				hide: !visible,
				autoHeight: false,
				wrapText: false,
				suppressNavigable: true,
				headerName: (field.displayName || field.name),
				cellRenderer: GridCellRendererComponent as any,
				sortable: true,
				comparator: () => 0  // disables actual sorting
			};

			if (this.configService.getConfig().search.searchableResultFields.includes(field.name)) {
				def.cellRenderer = GridSearchableCellRendererComponent;
			}
			else if (field.isLink) {
				// TODO autoHeight is not working when custom cellRenderer is used
				// See https://github.com/ag-grid/ag-grid/issues/3160
				def.cellRenderer = GridLinkCellRendererComponent;
				def.autoHeight = false;
				def.wrapText = false;
			}
			if(field.type == "DATE_RANGE"){
				//def.sortable = false;
			}
			this.columns.push(def);
		}

		// -- Tools Menu column --
		this.columns.push({
			colId: '_buttons',
			headerName: '',
			headerClass: 'ag-header-center',
			headerComponent: GridToolsHeaderRendererComponent,
			cellRenderer: GridMenuCellRendererComponent,
			width: 25,
			suppressHeaderMenuButton: true,
			sortable: false,
			suppressSizeToFit: true,
			suppressAutoSize: true,
			pinned: 'right',
			lockPosition: true,
			suppressMovable: true
		});
	}

	closeAllDropdowns(): void {
		this.dropdowns.forEach(dropdown => dropdown.close());
	}
}
